<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
	item: {
		type: Object,
		required: true,
	},
	depth: {
		type: Number,
		default: 0,
	},
})

const open = ref(false)
let closeTimer = null

const hasChildren = computed(() => props.item.children?.length > 0)
const isTop = computed(() => props.depth === 0)

// The panel must not clip nested submenus, which open as side flyouts.
const hasNestedSubmenu = computed(() => props.item.children?.some((child) => child.children?.length > 0))

const openMenu = () => {
	clearTimeout(closeTimer)
	open.value = true
}

const closeMenu = () => {
	closeTimer = setTimeout(() => (open.value = false), 80)
}

const rowBase = 'font-poppins text-sm font-semibold uppercase text-snk-dark-300 transition-colors duration-200'

const triggerBind = computed(() => {
	if (props.item.type === 'router') return { to: props.item.to, 'exact-active-class': 'text-snk-blue-500' }
	if (props.item.type === 'external') return { href: props.item.href, target: props.item.target }
	return {}
})

const triggerTag = computed(() => {
	if (props.item.type === 'router') return 'router-link'
	if (props.item.type === 'external') return 'a'
	return 'div'
})

const triggerClass = computed(() => {
	const clickable = props.item.type === 'label' ? 'cursor-default' : 'cursor-pointer'
	const activeColor = open.value ? 'text-snk-blue-500' : ''

	if (isTop.value) {
		return ['flex items-center gap-1 px-2 py-1', rowBase, clickable, 'hover:text-snk-blue-500', activeColor]
	}

	return ['flex items-center justify-between gap-2 px-4 py-2.5', rowBase, clickable, 'hover:bg-snk-light-900/40 hover:text-snk-blue-500', activeColor]
})

const leafClass = computed(() => {
	if (isTop.value) {
		return ['flex items-center gap-1.5 text-nowrap px-2 py-1', rowBase, 'hover:text-snk-blue-500']
	}

	return ['flex items-center gap-1.5 text-nowrap px-4 py-2.5', rowBase, 'hover:bg-snk-light-900/40 hover:text-snk-blue-500']
})
</script>

<template>
	<div :class="hasChildren ? 'relative' : ''" @mouseenter="hasChildren && openMenu()" @mouseleave="hasChildren && closeMenu()">
		<!-- Item with children: trigger + dropdown panel -->
		<template v-if="hasChildren">
			<component :is="triggerTag" v-bind="triggerBind" :class="triggerClass">
				<span class="flex items-center gap-1.5">
					{{ item.label }}
					<i v-if="item.showIcon" class="fas fa-arrow-up-right-from-square -mt-1 text-xs" />
				</span>
				<i class="fas text-[10px] transition-transform duration-200" :class="[isTop ? 'fa-chevron-down' : 'fa-chevron-right', { 'rotate-180': isTop && open }]" />
			</component>

			<div
				:class="[
					'absolute z-50 transition-all duration-150',
					isTop ? 'left-0 top-full pt-3' : 'left-full top-0 pl-2',
					open ? 'visible opacity-100' : 'invisible opacity-0',
				]"
			>
				<div class="flex min-w-44 flex-col divide-y divide-snk-light-900 rounded-md border border-snk-light-900 bg-snk-light-500 shadow-lg shadow-snk-dark-900/10" :class="hasNestedSubmenu ? 'overflow-visible' : 'overflow-hidden'">
					<DesktopMenuItem v-for="(child, i) in item.children" :key="child.label + i" :item="child" :depth="depth + 1" />
				</div>
			</div>
		</template>

		<!-- Leaf item -->
		<template v-else>
			<router-link v-if="item.type === 'router'" :to="item.to" :class="leafClass" exact-active-class="text-snk-blue-500">
				{{ item.label }}
			</router-link>
			<a v-else-if="item.type === 'external'" :href="item.href" :target="item.target" :class="leafClass">
				{{ item.label }}
				<i v-if="item.showIcon" class="fas fa-arrow-up-right-from-square -mt-1" :class="isTop ? '' : 'text-xs'" />
			</a>
			<span v-else :class="leafClass">{{ item.label }}</span>
		</template>
	</div>
</template>

<style scoped>
@reference '../../../css/theme.css';
</style>
